import { formatNumber, formatRange } from './format';
import { convertAmount } from './units';
import type {
  Concentration,
  DoseRule,
  DoseUnit,
  Medication,
  Patient,
  PatientPopulation,
  Population,
} from './types';

export type DoseStatus = 'ok' | 'needs-weight' | 'not-applicable';

export interface DoseCalculation {
  status: DoseStatus;
  /** Reasons the rule does not apply to this patient. */
  issues: string[];
  dose?: { low: number; high?: number; unit: DoseUnit };
  /** Present for infusions with a usable concentration. */
  rateMlPerHr?: { low: number; high?: number };
  /** Volume (mL) or count of discrete units (tablets, sprays) to administer. */
  quantity?: { low: number; high?: number; unit: string };
  concentration?: Concentration;
  capped: boolean;
  steps: string[];
  warnings: string[];
  info: string[];
}

export const NEONATE_MAX_AGE_YEARS = 28 / 365;
export const DEFAULT_PEDIATRIC_MAX_AGE_YEARS = 18;

export function inferPopulation(
  patient: Patient,
  pediatricMaxAgeYears = DEFAULT_PEDIATRIC_MAX_AGE_YEARS,
): PatientPopulation | undefined {
  if (patient.population) return patient.population;
  if (patient.ageYears === undefined) return undefined;
  if (patient.ageYears < NEONATE_MAX_AGE_YEARS) return 'neonate';
  if (patient.ageYears < pediatricMaxAgeYears) return 'pediatric';
  return 'adult';
}

/** Neonates may receive pediatric rules when no neonate-specific rule exists. */
export function populationMatches(rule: Population, patient: PatientPopulation | undefined): boolean {
  if (rule === 'all' || patient === undefined) return true;
  if (patient === 'neonate') return rule === 'neonate' || rule === 'pediatric';
  return rule === patient;
}

export function describeRuleDose(rule: DoseRule): string {
  const perKg = rule.kind === 'weight' || (rule.kind === 'infusion' && rule.perKg) ? '/kg' : '';
  const perTime = rule.kind === 'infusion' ? `/${rule.rateBasis ?? 'min'}` : '';
  const max = rule.maxSingle !== undefined ? ` (max ${formatNumber(rule.maxSingle)} ${rule.unit})` : '';
  return `${formatRange(rule.dose, rule.doseHigh)} ${rule.unit}${perKg}${perTime}${max}`;
}

function applicabilityIssues(rule: DoseRule, patient: Patient): string[] {
  const issues: string[] = [];
  const population = inferPopulation(patient);
  if (!populationMatches(rule.population, population)) {
    issues.push(`Written for ${rule.population} patients`);
  }
  const w = patient.weightKg;
  if (w !== undefined) {
    if (rule.minWeightKg !== undefined && w < rule.minWeightKg) {
      issues.push(`Requires weight ≥ ${formatNumber(rule.minWeightKg)} kg`);
    }
    if (rule.maxWeightKg !== undefined && w >= rule.maxWeightKg) {
      issues.push(`For weight < ${formatNumber(rule.maxWeightKg)} kg`);
    }
  }
  const age = patient.ageYears;
  if (age !== undefined) {
    if (rule.minAgeYears !== undefined && age < rule.minAgeYears) {
      issues.push(`Requires age ≥ ${formatNumber(rule.minAgeYears)} years`);
    }
    if (rule.maxAgeYears !== undefined && age >= rule.maxAgeYears) {
      issues.push(`For age < ${formatNumber(rule.maxAgeYears)} years`);
    }
  }
  return issues;
}

function concentrationPerMl(conc: Concentration): number | undefined {
  return conc.volumeMl ? conc.amount / conc.volumeMl : undefined;
}

export function describeConcentration(conc: Concentration): string {
  if (conc.label) return conc.label;
  if (conc.volumeMl) {
    const perMl = conc.amount / conc.volumeMl;
    return conc.volumeMl === 1
      ? `${formatNumber(conc.amount)} ${conc.unit}/mL`
      : `${formatNumber(conc.amount)} ${conc.unit}/${formatNumber(conc.volumeMl)} mL (${formatNumber(perMl)} ${conc.unit}/mL)`;
  }
  return `${formatNumber(conc.amount)} ${conc.unit} per ${conc.form ?? 'unit'}`;
}

/**
 * Calculates a single dose rule for a patient. Pure and deterministic: every number shown to
 * a provider is accompanied by the arithmetic that produced it (`steps`) so it can be checked.
 */
export function calculateDose(
  rule: DoseRule,
  patient: Patient,
  concentrationOverride?: Concentration,
): DoseCalculation {
  const result: DoseCalculation = {
    status: 'ok',
    issues: applicabilityIssues(rule, patient),
    capped: false,
    steps: [],
    warnings: [],
    info: [],
  };
  if (result.issues.length > 0) {
    result.status = 'not-applicable';
    return result;
  }

  const weight = patient.weightKg;
  const needsWeight = rule.kind === 'weight' || (rule.kind === 'infusion' && rule.perKg);
  if (needsWeight && (weight === undefined || weight <= 0)) {
    result.status = 'needs-weight';
    return result;
  }

  const conc = concentrationOverride ?? rule.concentration;
  result.concentration = conc;

  if (rule.kind === 'infusion') {
    return calculateInfusionRule(rule, weight, conc, result);
  }

  let low = rule.dose;
  let high = rule.doseHigh;
  if (rule.kind === 'weight' && weight !== undefined) {
    low = rule.dose * weight;
    high = rule.doseHigh !== undefined ? rule.doseHigh * weight : undefined;
    result.steps.push(
      `${formatRange(rule.dose, rule.doseHigh)} ${rule.unit}/kg × ${formatNumber(weight)} kg = ${formatRange(low, high)} ${rule.unit}`,
    );
  } else {
    result.steps.push(`Fixed dose: ${formatRange(low, high)} ${rule.unit}`);
  }

  if (rule.maxSingle !== undefined) {
    const max = rule.maxSingle;
    if (low > max || (high !== undefined && high > max)) {
      result.capped = true;
      low = Math.min(low, max);
      high = high !== undefined ? Math.min(high, max) : undefined;
      if (high !== undefined && high === low) high = undefined;
      result.steps.push(`Limited to maximum single dose of ${formatNumber(max)} ${rule.unit}`);
      result.warnings.push(
        `Calculated dose exceeds the maximum single dose — capped at ${formatNumber(max)} ${rule.unit}.`,
      );
    }
  } else if (rule.kind === 'weight') {
    result.info.push('No maximum single dose is defined for this rule.');
  }

  result.dose = { low, high, unit: rule.unit };

  if (conc) {
    const lowInConcUnit = convertAmount(low, rule.unit, conc.unit);
    const highInConcUnit = high !== undefined ? convertAmount(high, rule.unit, conc.unit) : undefined;
    const perMl = concentrationPerMl(conc);
    if (lowInConcUnit === null || highInConcUnit === null) {
      result.warnings.push(`Concentration unit (${conc.unit}) is not compatible with dose unit (${rule.unit}).`);
    } else if (perMl !== undefined) {
      const volLow = lowInConcUnit / perMl;
      const volHigh = highInConcUnit !== undefined ? highInConcUnit / perMl : undefined;
      result.quantity = { low: volLow, high: volHigh, unit: 'mL' };
      result.steps.push(
        `${formatRange(lowInConcUnit, highInConcUnit)} ${conc.unit} ÷ ${formatNumber(perMl)} ${conc.unit}/mL = ${formatRange(volLow, volHigh)} mL`,
      );
    } else {
      const unitLabel = conc.form ?? 'unit';
      const qLow = lowInConcUnit / conc.amount;
      const qHigh = highInConcUnit !== undefined ? highInConcUnit / conc.amount : undefined;
      result.quantity = { low: qLow, high: qHigh, unit: unitLabel };
      result.steps.push(
        `${formatRange(lowInConcUnit, highInConcUnit)} ${conc.unit} ÷ ${formatNumber(conc.amount)} ${conc.unit} per ${unitLabel} = ${formatRange(qLow, qHigh)} ${unitLabel}(s)`,
      );
    }
  }

  if (rule.maxTotal !== undefined) {
    result.info.push(`Maximum cumulative dose: ${formatNumber(rule.maxTotal)} ${rule.unit}.`);
  }
  if (rule.repeatMinutes !== undefined) {
    const repeats = rule.maxRepeats !== undefined ? ` (up to ${rule.maxRepeats} repeat${rule.maxRepeats === 1 ? '' : 's'})` : '';
    result.info.push(`May repeat every ${rule.repeatMinutes} min${repeats}.`);
  }
  return result;
}

function calculateInfusionRule(
  rule: DoseRule,
  weight: number | undefined,
  conc: Concentration | undefined,
  result: DoseCalculation,
): DoseCalculation {
  const basis = rule.rateBasis ?? 'min';
  const factor = rule.perKg && weight !== undefined ? weight : 1;
  const low = rule.dose * factor;
  const high = rule.doseHigh !== undefined ? rule.doseHigh * factor : undefined;
  const perKgLabel = rule.perKg ? '/kg' : '';
  if (rule.perKg && weight !== undefined) {
    result.steps.push(
      `${formatRange(rule.dose, rule.doseHigh)} ${rule.unit}/kg/${basis} × ${formatNumber(weight)} kg = ${formatRange(low, high)} ${rule.unit}/${basis}`,
    );
  } else {
    result.steps.push(`Rate: ${formatRange(low, high)} ${rule.unit}${perKgLabel}/${basis}`);
  }
  result.dose = { low, high, unit: rule.unit };

  const perMl = conc ? concentrationPerMl(conc) : undefined;
  if (!conc || perMl === undefined) {
    result.info.push('Select a concentration to calculate the pump rate.');
    return result;
  }
  const toPerHour = basis === 'min' ? 60 : 1;
  const lowConc = convertAmount(low * toPerHour, rule.unit, conc.unit);
  const highConc = high !== undefined ? convertAmount(high * toPerHour, rule.unit, conc.unit) : undefined;
  if (lowConc === null || highConc === null) {
    result.warnings.push(`Concentration unit (${conc.unit}) is not compatible with dose unit (${rule.unit}).`);
    return result;
  }
  const rateLow = lowConc / perMl;
  const rateHigh = highConc !== undefined ? highConc / perMl : undefined;
  result.rateMlPerHr = { low: rateLow, high: rateHigh };
  result.steps.push(
    `${formatRange(lowConc, highConc)} ${conc.unit}/hr ÷ ${formatNumber(perMl)} ${conc.unit}/mL = ${formatRange(rateLow, rateHigh)} mL/hr`,
  );
  return result;
}

export interface MedicationDoseResult {
  medication: Medication;
  rule: DoseRule;
  calculation: DoseCalculation;
}

/** Calculates every dose rule for a medication, applicable rules first. */
export function calculateMedication(medication: Medication, patient: Patient): MedicationDoseResult[] {
  const order: Record<DoseStatus, number> = { ok: 0, 'needs-weight': 1, 'not-applicable': 2 };
  return medication.doses
    .map((rule) => ({ medication, rule, calculation: calculateDose(rule, patient) }))
    .sort((a, b) => order[a.calculation.status] - order[b.calculation.status]);
}

/** All applicable rules across medications carrying any of the given tags. */
export function rulesByTag(medications: Medication[], tags: string[], patient: Patient): MedicationDoseResult[] {
  const out: MedicationDoseResult[] = [];
  for (const medication of medications) {
    for (const rule of medication.doses) {
      if (!rule.tags?.some((t) => tags.includes(t))) continue;
      const calculation = calculateDose(rule, patient);
      if (calculation.status === 'not-applicable') continue;
      out.push({ medication, rule, calculation });
    }
  }
  return out;
}

export interface DripRateInput {
  volumeMl: number;
  minutes: number;
  dropFactor: number;
}

/** Gravity drip rate in drops per minute. */
export function dripRate({ volumeMl, minutes, dropFactor }: DripRateInput): number {
  if (minutes <= 0) return NaN;
  return (volumeMl * dropFactor) / minutes;
}

export interface InfusionRateInput {
  dose: number;
  doseUnit: DoseUnit;
  perKg: boolean;
  rateBasis: 'min' | 'hr';
  weightKg?: number;
  concentration: Concentration;
}

/** Pump rate in mL/hr for a desired infusion dose. Returns NaN when inputs are incomplete. */
export function infusionRateMlPerHr(input: InfusionRateInput): number {
  const { dose, doseUnit, perKg, rateBasis, weightKg, concentration } = input;
  if (perKg && (weightKg === undefined || weightKg <= 0)) return NaN;
  const perMl = concentrationPerMl(concentration);
  if (perMl === undefined || perMl <= 0) return NaN;
  const amountPerHr = dose * (perKg ? (weightKg as number) : 1) * (rateBasis === 'min' ? 60 : 1);
  const converted = convertAmount(amountPerHr, doseUnit, concentration.unit);
  if (converted === null) return NaN;
  return converted / perMl;
}

/** Inverse of infusionRateMlPerHr: the dose delivered at a given pump rate. */
export function doseFromRate(input: Omit<InfusionRateInput, 'dose'> & { rateMlPerHr: number }): number {
  const { rateMlPerHr, doseUnit, perKg, rateBasis, weightKg, concentration } = input;
  if (perKg && (weightKg === undefined || weightKg <= 0)) return NaN;
  const perMl = concentrationPerMl(concentration);
  if (perMl === undefined) return NaN;
  const amountPerHrConcUnit = rateMlPerHr * perMl;
  const amountPerHr = convertAmount(amountPerHrConcUnit, concentration.unit, doseUnit);
  if (amountPerHr === null) return NaN;
  return amountPerHr / (rateBasis === 'min' ? 60 : 1) / (perKg ? (weightKg as number) : 1);
}
